import React, { useRef, useState, useEffect } from 'react';
import { Pencil, Eraser, Undo, Redo, Check, ChevronLeft, AlertTriangle } from 'lucide-react';

interface FeedbackAnnotationProps {
  initialImage: string;
  onConfirm: (editedImage: string) => void;
  onCancel: () => void;
}

interface Stroke {
  type: 'pencil' | 'eraser';
  points: { x: number; y: number }[];
  color: string;
  width: number;
}

export function FeedbackAnnotation({ initialImage, onConfirm, onCancel }: FeedbackAnnotationProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const baseImageRef = useRef<HTMLImageElement | null>(null);

  const [tool, setTool] = useState<'pencil' | 'eraser'>('pencil');
  const [history, setHistory] = useState<Stroke[]>([]);
  const [redoStack, setRedoStack] = useState<Stroke[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentStroke, setCurrentStroke] = useState<Stroke | null>(null);
  
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      baseImageRef.current = img;
      redrawCanvas();
    };
    img.src = initialImage;
  }, [initialImage]);

  const redrawCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const container = containerRef.current;
    
    if (!canvas || !ctx || !baseImageRef.current || !container) return;

    // Set canvas dimensions to match container while keeping aspect ratio
    const img = baseImageRef.current;
    const containerRatio = container.clientWidth / container.clientHeight;
    const imgRatio = img.width / img.height;

    let targetWidth = container.clientWidth;
    let targetHeight = container.clientHeight;

    if (containerRatio > imgRatio) {
      targetWidth = targetHeight * imgRatio;
    } else {
      targetHeight = targetWidth / imgRatio;
    }

    canvas.width = targetWidth;
    canvas.height = targetHeight;

    // Draw base image
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    // Draw all strokes
    const drawStroke = (stroke: Stroke) => {
      if (stroke.points.length === 0) return;
      
      ctx.beginPath();
      ctx.moveTo(stroke.points[0].x * canvas.width, stroke.points[0].y * canvas.height);
      
      for (let i = 1; i < stroke.points.length; i++) {
        ctx.lineTo(stroke.points[i].x * canvas.width, stroke.points[i].y * canvas.height);
      }
      
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      if (stroke.type === 'eraser') {
        ctx.globalCompositeOperation = 'destination-out';
        ctx.lineWidth = 20;
        ctx.strokeStyle = 'rgba(0,0,0,1)';
      } else {
        ctx.globalCompositeOperation = 'source-over';
        ctx.lineWidth = stroke.width;
        ctx.strokeStyle = stroke.color;
      }
      
      ctx.stroke();
      ctx.globalCompositeOperation = 'source-over'; // reset
    };

    history.forEach(drawStroke);
    
    if (currentStroke) {
      drawStroke(currentStroke);
    }
  };

  // Re-draw whenever strokes change
  useEffect(() => {
    redrawCanvas();
  }, [history, currentStroke]);

  // Handle resize
  useEffect(() => {
    const handleResize = () => redrawCanvas();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getCanvasPoint = (e: React.PointerEvent | PointerEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / canvas.width,
      y: (e.clientY - rect.top) / canvas.height
    };
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    setIsDrawing(true);
    setCurrentStroke({
      type: tool,
      points: [getCanvasPoint(e)],
      color: '#ff003c', // Red color for annotation
      width: 4
    });
    setRedoStack([]); // Clear redo stack on new action
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDrawing || !currentStroke) return;
    setCurrentStroke(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        points: [...prev.points, getCanvasPoint(e)]
      };
    });
  };

  const handlePointerUp = () => {
    if (isDrawing && currentStroke) {
      setHistory(prev => [...prev, currentStroke]);
    }
    setIsDrawing(false);
    setCurrentStroke(null);
  };

  const handleUndo = () => {
    if (history.length === 0) return;
    const lastStroke = history[history.length - 1];
    setHistory(prev => prev.slice(0, -1));
    setRedoStack(prev => [...prev, lastStroke]);
  };

  const handleRedo = () => {
    if (redoStack.length === 0) return;
    const nextStroke = redoStack[redoStack.length - 1];
    setRedoStack(prev => prev.slice(0, -1));
    setHistory(prev => [...prev, nextStroke]);
  };

  const handleConfirm = () => {
    // Return a composite image of base + drawing with a black background
    const canvas = canvasRef.current;
    if (!canvas) return;

    // We can just use the canvas's current content, but we might want to ensure a solid background
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = canvas.width;
    exportCanvas.height = canvas.height;
    const ctx = exportCanvas.getContext('2d');
    if (ctx && baseImageRef.current) {
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
        ctx.drawImage(canvas, 0, 0);
        
        onConfirm(exportCanvas.toDataURL('image/jpeg', 0.8));
    }
  };

  const handleBack = () => {
    if (history.length > 0) {
      setShowDiscardConfirm(true);
    } else {
      onCancel();
    }
  };

  return (
    <div className="fixed inset-0 z-[1000000] bg-black flex flex-col animate-in fade-in duration-200">
      {/* Top Toolbar */}
      <div className="h-14 bg-[#0a0e27] border-b border-[#00d9ff]/20 flex items-center justify-between px-4 shrink-0">
        <button 
          onClick={handleBack}
          className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
        >
          <ChevronLeft size={20} />
          <span className="text-xs font-bold uppercase tracking-wider hidden md:block">Voltar</span>
        </button>

        <div className="flex items-center gap-2 bg-black/50 p-1 rounded-lg border border-white/10">
          <button
            onClick={() => setTool('pencil')}
            className={`p-2 rounded transition-colors ${tool === 'pencil' ? 'bg-[#ff003c]/20 text-[#ff003c]' : 'text-white/50 hover:text-white hover:bg-white/5'}`}
            title="Lápis"
          >
            <Pencil size={18} />
          </button>
          <button
            onClick={() => setTool('eraser')}
            className={`p-2 rounded transition-colors ${tool === 'eraser' ? 'bg-white/20 text-white' : 'text-white/50 hover:text-white hover:bg-white/5'}`}
            title="Borracha"
          >
            <Eraser size={18} />
          </button>
          <div className="w-px h-6 bg-white/10 mx-1"></div>
          <button
            onClick={handleUndo}
            disabled={history.length === 0}
            className="p-2 rounded text-white/50 hover:text-white hover:bg-white/5 disabled:opacity-30 disabled:hover:bg-transparent"
            title="Desfazer"
          >
            <Undo size={18} />
          </button>
          <button
            onClick={handleRedo}
            disabled={redoStack.length === 0}
            className="p-2 rounded text-white/50 hover:text-white hover:bg-white/5 disabled:opacity-30 disabled:hover:bg-transparent"
            title="Refazer"
          >
            <Redo size={18} />
          </button>
        </div>

        <button 
          onClick={handleConfirm}
          className="flex items-center gap-2 px-4 py-1.5 bg-[#00ffa3]/20 hover:bg-[#00ffa3]/30 text-[#00ffa3] border border-[#00ffa3]/50 rounded text-xs font-bold uppercase tracking-wider transition-colors"
        >
          <Check size={16} />
          <span className="hidden md:block">Concluir</span>
        </button>
      </div>

      {/* Canvas Area */}
      <div 
        ref={containerRef} 
        className="flex-1 relative overflow-hidden flex items-center justify-center cursor-crosshair touch-none"
      >
        <canvas
          ref={canvasRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerOut={handlePointerUp}
          className="max-w-full max-h-full"
          style={{ touchAction: 'none' }}
        />
      </div>

      {/* Discard Confirmation Modal */}
      {showDiscardConfirm && (
        <div className="absolute inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0a0e27] border border-[#00d9ff]/30 p-6 rounded-lg max-w-sm w-full animate-in zoom-in-95 duration-200 shadow-2xl">
            <div className="flex items-center gap-3 text-red-400 mb-4">
              <AlertTriangle size={24} />
              <h3 className="text-sm font-bold uppercase tracking-wider">Descartar Alterações?</h3>
            </div>
            <p className="text-white/70 text-xs leading-relaxed mb-6">
              Você fez anotações nesta imagem. Se voltar agora, elas serão perdidas.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDiscardConfirm(false)}
                className="flex-1 py-2 text-xs font-bold uppercase tracking-wider text-white/50 hover:text-white bg-white/5 hover:bg-white/10 rounded transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  setShowDiscardConfirm(false);
                  onCancel();
                }}
                className="flex-1 py-2 text-xs font-bold uppercase tracking-wider text-red-400 border border-red-400/30 hover:bg-red-400/10 rounded transition-colors"
              >
                Sim, Descartar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
